import { METER_LABELS } from 'features/customers/constants/contactsActivity'
import { countBy, groupBy, keyBy, minBy, uniq, uniqBy } from 'lodash'

import { Contacts } from '@albacross/types'

export type OutcomeTone = 'success' | 'default' | 'warning' | 'info' | 'error'

// What a CSM must see first wins an item's chip: something wrong, then what was charged.
const CHIP_RANK = [
  'Stuck',
  'Refused',
  'Failed',
  'Waiting',
  'At limit',
  'Late',
  'Charged',
  'Free',
  'Timed out',
  'Nothing',
] as const

type Chip = (typeof CHIP_RANK)[number]

export type RequestDescription = { chip: Chip; tone: OutcomeTone; sentence: string }

const CREDIT_NOUNS: Record<Contacts.CreditMeter, string> = { email: 'email credits', phone: 'phone credits' }
const METER_NOUNS: Record<Contacts.CreditMeter, string> = { email: 'emails', phone: 'phones' }

const people = (count: number) => `${count} ${count === 1 ? 'person' : 'people'}`
export const minutesBetween = (from: string, to: number) => Math.max(0, Math.round((to - Date.parse(from)) / 60_000))
const reservedMinutes = ({ createdAt, expiresAt }: Contacts.CreditRequest) =>
  minutesBetween(createdAt, Date.parse(expiresAt))

const nothing = (sentence: string): RequestDescription => ({ chip: 'Nothing', tone: 'default', sentence })
const failed = (sentence: string, tone: OutcomeTone = 'error'): RequestDescription => ({
  chip: 'Failed',
  tone,
  sentence,
})

const peopleDelivered = (delivered: number, charged: number, free: number) => {
  if (!charged) return `${people(delivered)}, free: already charged this month`
  if (!free) return `${people(delivered)}, ${charged} charged`
  return `${people(delivered)}: ${charged} charged, ${free} free (already charged this month)`
}

const held = (request: Contacts.CreditRequest, now: number): RequestDescription => {
  const waited = minutesBetween(request.createdAt, now)
  if (now - Date.parse(request.expiresAt) > Contacts.STUCK_AFTER_EXPIRY_MS) {
    return {
      chip: 'Stuck',
      tone: 'error',
      sentence: `Waiting for ${waited} min: this should have expired. Check the sweep.`,
    }
  }
  const sentence =
    request.reason === 'breaker_open'
      ? `Waiting: provider down, paused by us (${waited} min).`
      : request.reason === 'rate_limited'
        ? `Waiting: provider rate limit (${waited} min).`
        : `Waiting for the provider (${waited} min).`
  return { chip: 'Waiting', tone: 'warning', sentence }
}

const released = ({ reason, meter, domain, returned }: Contacts.CreditRequest): RequestDescription => {
  switch (reason) {
    case 'denied':
      return { chip: 'Refused', tone: 'error', sentence: `Refused: no ${CREDIT_NOUNS[meter]} left.` }
    case 'none_found':
      return nothing(`Nobody found${domain ? ` for ${domain}` : ''}. Nothing charged.`)
    case 'filtered_out':
      return nothing(
        returned
          ? `${people(returned)} found, the workflow's filters removed all of them. Nothing charged.`
          : "The workflow's filters removed everyone found. Nothing charged.",
      )
    case 'provider_failed':
      return failed('The provider failed. Nothing charged.')
    case 'bad_domain':
      return failed(`The provider rejected ${domain ?? 'the company'} as a domain. Nothing charged.`)
    case 'breaker_open':
      return failed('Provider down, paused by us: credits given back until the retry. Nothing charged.', 'warning')
    case 'rate_limited':
      return failed('Provider rate limit: credits given back until the retry. Nothing charged.', 'warning')
    default:
      return nothing('Nothing delivered, credits given back. Nothing charged.')
  }
}

const confirmed = (request: Contacts.CreditRequest): RequestDescription => {
  const { meter, late, reason, origin, requested, granted } = request
  const delivered = request.delivered ?? 0
  const charged = request.charged ?? 0
  if (!delivered) return nothing(`No ${METER_NOUNS[meter]} delivered. Nothing charged.`)

  const summary = peopleDelivered(delivered, charged, request.free ?? 0)
  const paid = (sentence: string): RequestDescription =>
    charged ? { chip: 'Charged', tone: 'success', sentence } : { chip: 'Free', tone: 'default', sentence }
  if (origin.kind === 'auto_engage') return paid(`Bought through Auto-Engage: ${summary}.`)
  if (late) {
    const over = charged ? ` The month can be over its limit by ${charged}.` : ''
    return {
      chip: 'Late',
      tone: 'info',
      sentence: `Delivered late, after its reservation had ended: ${summary}.${over}`,
    }
  }
  // The reason is shared by a request's email and phone reservations; only the cut one was granted less.
  if (reason === 'partial_grant' && granted < requested) {
    return {
      chip: 'At limit',
      tone: 'warning',
      sentence: `Delivered ${summary}. Asked for ${requested}, only ${granted} ${CREDIT_NOUNS[meter]} were left.`,
    }
  }
  return paid(`Delivered ${summary}.`)
}

export const describeRequest = (request: Contacts.CreditRequest, now: number): RequestDescription => {
  switch (request.state) {
    case 'held':
      return held(request, now)
    case 'expired':
      return {
        chip: 'Timed out',
        tone: 'default',
        sentence: `No answer within ${reservedMinutes(request)} min: credits given back. Nothing charged.`,
      }
    case 'released':
      return released(request)
    case 'confirmed':
      return confirmed(request)
  }
}

export const noJobNote = ({ state, reason, origin }: Contacts.CreditRequest) => {
  if (origin.kind === 'auto_engage') return 'Bought by Auto-Engage; no provider job recorded here.'
  if (reason === 'denied') return 'None: refused before any search.'
  if (state === 'held') return 'None yet.'
  if (state !== 'confirmed') return 'None recorded.'
  return 'None recorded: answered from our cache, or no search was needed. The cache lowers our cost; the customer is charged as usual.'
}

type Described = RequestDescription & { request: Contacts.CreditRequest }

const mostTelling = (described: Described[]) => minBy(described, ({ chip }) => CHIP_RANK.indexOf(chip)) as Described

const isDelivered = ({ request }: Described) => request.state === 'confirmed' && !!request.delivered

const companyPhrase = (described: Described[]) => {
  if (described.some(isDelivered)) return 'delivered'
  const { chip, request } = mostTelling(described)
  if (chip === 'Nothing') {
    if (request.reason === 'none_found') return 'found nobody'
    if (request.reason === 'filtered_out') return 'filtered out'
    return 'delivered nothing'
  }
  return chip === 'Stuck' ? 'waiting' : chip.toLowerCase()
}

const companies = (count: number) => `${count} ${count === 1 ? 'company' : 'companies'}`

const severalCompanies = (described: Described[]) => {
  const byCompany = groupBy(described, ({ request }) => request.domain ?? '')
  const phrases = Object.values(byCompany).map(companyPhrase)
  const parts = Object.entries(countBy(phrases)).map(([phrase, count]) => `${count} ${phrase}`)
  // A refusal under a company that still got something would otherwise be hidden behind "delivered".
  const hiddenRefusals = uniqBy(
    described.filter(({ chip, request }) => chip === 'Refused' && byCompany[request.domain ?? ''].some(isDelivered)),
    ({ request }) => request.meter,
  )
  return [`${companies(Object.keys(byCompany).length)}: ${parts.join(', ')}.`, ...hiddenRefusals.map(d => d.sentence)]
}

const oneCompany = (described: Described[]) => {
  const meters = uniq(described.map(({ request }) => request.meter))
  return described.map(({ request, sentence }) =>
    meters.length > 1 ? `${METER_LABELS[request.meter]}: ${sentence}` : sentence,
  )
}

// A later reservation of the same company and meter (a retry without filters) supersedes the earlier one.
const latestPerCompanyAndMeter = (requests: Contacts.CreditRequest[]) =>
  Object.values(keyBy(requests, ({ domain, meter }) => `${domain}:${meter}`))

export const describeItem = (item: Contacts.CreditRequestItem, now: number): RequestDescription => {
  if (item.requests.length === 1) return describeRequest(item.requests[0], now)
  const described = latestPerCompanyAndMeter(item.requests).map(request => ({
    request,
    ...describeRequest(request, now),
  }))
  const { chip, tone } = mostTelling(described)
  const retried = item.requests.some(({ origin }) => origin.kind === 'workflow' && origin.noFilter)
  const sentences = [
    ...(item.domains.length > 1 ? severalCompanies(described) : oneCompany(described)),
    ...(retried ? ["Retried without the workflow's filters."] : []),
  ]
  return { chip, tone, sentence: sentences.join(' ') }
}
